import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const InviteTeamMember = ({ onInviteMember }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'Designer', label: 'Designer', description: 'Can create and manage design projects' },
    { value: 'Sales Rep', label: 'Sales Representative', description: 'Can communicate with clients and view projects' },
    { value: 'Manager', label: 'Manager', description: 'Can manage projects and team members' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!role) {
      newErrors.role = 'Please select a role';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      await onInviteMember({
        email,
        role,
        message: message?.trim() || `You've been invited to join our jewelry design team as a ${role}.`
      });
      
      // Reset form
      setEmail('');
      setRole('');
      setMessage('');
      setErrors({});
    } catch (error) {
      setErrors({ submit: 'Failed to send invitation. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 elevation-1">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="UserPlus" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h2 className="text-lg font-heading text-foreground">Invite Team Member</h2>
          <p className="text-sm text-muted-foreground">Add a new member to your jewelry design team</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Email Address"
            type="email"
            placeholder="colleague@company.com"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            error={errors?.email}
            required
          />
          
          <Select
            label="Role"
            placeholder="Select a role"
            options={roleOptions}
            value={role}
            onChange={setRole}
            error={errors?.role}
            required
          />
        </div>

        <Input
          label="Personal Message (Optional)"
          type="text"
          placeholder="Welcome to our team! We're excited to work with you."
          value={message}
          onChange={(e) => setMessage(e?.target?.value)}
          description="Add a personal touch to the invitation email"
        />

        {errors?.submit && (
          <div className="flex items-center space-x-2 text-error text-sm">
            <Icon name="AlertCircle" size={16} />
            <span>{errors?.submit}</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="text-sm text-muted-foreground">
            <Icon name="Info" size={14} className="inline mr-1" />
            Invitation will be sent via email
          </div>
          
          <Button
            type="submit"
            loading={isLoading}
            iconName="Send"
            iconPosition="right"
            disabled={!email || !role}
          >
            Send Invitation
          </Button>
        </div>
      </form>
      {/* Role Permissions Preview */}
      {role && (
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-sm font-medium text-foreground mb-3">Role Permissions Preview</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {role === 'Designer' && (
              <>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>Create Projects</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>Upload Design Files</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>Access Digital Vault</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="X" size={14} />
                  <span>Manage Team</span>
                </div>
              </>
            )}
            
            {role === 'Sales Rep' && (
              <>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>Client Communication</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>View Projects</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="X" size={14} />
                  <span>Create Projects</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="X" size={14} />
                  <span>Manage Team</span>
                </div>
              </>
            )}
            
            {role === 'Manager' && (
              <>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>All Project Access</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>Team Management</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>Client Communication</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Check" size={14} />
                  <span>Access Digital Vault</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteTeamMember;